/** Turn the current header rows into one row per order line, using the lines already loaded for inline view. */

function detailsForOrder(orderNum, orderDetailsData) {
    if (orderNum == null || orderNum === '' || !orderDetailsData) return null;
    return orderDetailsData[orderNum]
        ?? orderDetailsData[String(orderNum)]
        ?? orderDetailsData[Number(orderNum)]
        ?? null;
}

function isArchivedLine(id, archivedIds) {
    if (!archivedIds || id == null) return false;
    return archivedIds.has(id) || archivedIds.has(String(id));
}

export function flattenOrdersToLines(headerRows, orderDetailsData, archivedIds) {
    const rows = [];
    const seenOrders = new Set();

    (headerRows || []).forEach((headerLine) => {
        if (!headerLine) return;
        const orderNum = headerLine?.order?.ordernum ?? headerLine?.ordernum ?? '';
        const orderKey = String(orderNum);
        if (orderKey && seenOrders.has(orderKey)) return;
        if (orderKey) seenOrders.add(orderKey);

        const details = detailsForOrder(orderNum, orderDetailsData);
        const source = Array.isArray(details) && details.length > 0
            ? [...details].sort((a, b) => (Number(a?.lineitem) || 0) - (Number(b?.lineitem) || 0))
            : [headerLine];

        source.forEach((raw) => {
            if (!raw || isArchivedLine(raw.id, archivedIds)) return;
            const line = raw.order ? raw : { ...raw, order: headerLine.order };
            rows.push({
                ...line,
                _headerLine: headerLine,
                _orderStatusLineId: headerLine.id,
            });
        });
    });

    return rows;
}

export function orderLinesForHeader(headerLine, orderDetailsData) {
    if (!headerLine) return [];
    const orderNum = headerLine?.order?.ordernum ?? headerLine?.ordernum;
    const details = detailsForOrder(orderNum, orderDetailsData);
    if (Array.isArray(details) && details.length > 0) return details;
    return [headerLine];
}

export function headerLinesMissingDetails(headerRows, orderDetailsData) {
    return (headerRows || []).some((headerLine) => {
        const orderNum = headerLine?.order?.ordernum ?? headerLine?.ordernum;
        if (orderNum == null || orderNum === '') return false;
        const details = detailsForOrder(orderNum, orderDetailsData);
        return !Array.isArray(details) || details.length === 0;
    });
}

function commentTime(comment) {
    const raw = comment?.createdAt
        || (comment?.dateStr && comment?.timeStr ? `${comment.dateStr} ${comment.timeStr}` : comment?.date)
        || 0;
    const time = new Date(raw).getTime();
    return Number.isFinite(time) ? time : 0;
}

function commentsForLine(line, commentsMap) {
    if (!line) return [];
    const extra = [
        ...(commentsMap?.[line.id] || []),
        ...(line.id != null ? (commentsMap?.[String(line.id)] || []) : []),
    ];
    return [...(line.comment || []), ...extra].filter(Boolean);
}

/** Header chat: every line's comments for this order, newest first. */
export function collectOrderComments(headerLine, orderDetailsData, commentsMap) {
    const seen = new Set();
    const merged = [];

    orderLinesForHeader(headerLine, orderDetailsData).forEach((line) => {
        const lineNo = line?.lineitem != null && String(line.lineitem) !== '' ? `Line ${line.lineitem}` : '';
        commentsForLine(line, commentsMap).forEach((comment) => {
            const id = comment.id != null ? String(comment.id) : '';
            const key = id || `${line.id}:${comment.comment}:${comment.dateStr}:${comment.timeStr}`;
            if (seen.has(key)) return;
            seen.add(key);
            merged.push({
                ...comment,
                orderLineId: comment.orderLineId || line.id,
                lineLabel: comment.lineLabel || lineNo,
            });
        });
    });

    merged.sort((a, b) => commentTime(b) - commentTime(a));
    return merged;
}

export function findCommentLineId(commentId, headerLine, orderDetailsData, commentsMap) {
    const id = String(commentId);
    const lines = orderLinesForHeader(headerLine, orderDetailsData);
    for (const line of lines) {
        if (commentsForLine(line, commentsMap).some((comment) => String(comment.id) === id)) {
            return line.id;
        }
    }
    return headerLine?.id;
}

export function findOrderLineById(lineId, lists, orderDetailsData) {
    const id = String(lineId);
    for (const list of lists || []) {
        if (!Array.isArray(list)) continue;
        const found = list.find((row) => String(row?.id) === id);
        if (found) return found;
    }
    if (orderDetailsData) {
        for (const rows of Object.values(orderDetailsData)) {
            if (!Array.isArray(rows)) continue;
            const found = rows.find((row) => String(row?.id) === id);
            if (found) return found;
        }
    }
    return null;
}
